import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Business,
  KeyboardArrowDown,
  Menu as MenuIcon,
  PointOfSaleOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import fallbackLogo from "../../assets/pawsi.jpeg";
import { useAuth } from "../../context/AuthContext";
import { hasAnyGeneralInventoryPermission, hasPermission } from "../../config/permissionUtils";
import { cashService, inventoryService } from "../../services";
import { emitCashSessionChanged, onCashSessionChanged } from "../../services/http/cashEvents";
import {
  buildEmptyCurrentSession,
  formatDateTime,
  normalizeCurrentSession,
  normalizeSessionDetail,
  toInputMoney,
  toMoney,
} from "../../Pages/Cash/cashSessionShared";
import CustomSnackBar from "../CustomSnackBar";
import BranchSwitchMenu from "./BranchSwitchMenu";
import UserMenu from "./CustomUserProfile";
import POSDialog from "../sale/POSDialog";

const EMPTY_SESSION_DATA = buildEmptyCurrentSession();

const movementReasonOptions = [
  { value: "EXPENSE", label: "Gasto" },
  { value: "WITHDRAWAL", label: "Retiro" },
  { value: "OTHER", label: "Otro" },
];

const movementChannelOptions = [
  { value: "CASH", label: "Efectivo" },
  { value: "CARD", label: "Tarjeta" },
  { value: "TRANSFER", label: "Transferencia" },
];

function CashPill({
  hasOpenSession,
  cashRegisterName,
  openedAt,
  openedByName,
  isOwnSession,
  onActionClick,
  onMovementClick,
  canRunAction = false,
  canCreateMovement = false,
  disabled = false,
  mode = "desktop",
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const isTablet = mode === "tablet";
  const isMobile = mode === "mobile";
  const label = hasOpenSession
    ? mode === "desktop"
      ? "Caja abierta"
      : mode === "tablet"
        ? "Abierta"
        : "Caja"
    : mode === "desktop"
      ? "Caja cerrada"
      : mode === "tablet"
        ? "Cerrada"
        : "Caja";

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        disabled={disabled}
        sx={{
          borderRadius: 999,
          px: isMobile ? 0.85 : isTablet ? 1.1 : 1.5,
          py: isMobile ? 0.35 : 0.5,
          minWidth: 0,
          maxWidth: isMobile ? 92 : isTablet ? 120 : 200,
          minHeight: isMobile ? 30 : 34,
          bgcolor: alpha(theme.palette.common.white, 0.08),
          border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 0.45 : 0.8,
          transition: "all 0.2s",
          color: "inherit",
          flexShrink: 0,
          overflow: "hidden",
          "&:hover": {
            bgcolor: alpha(theme.palette.common.white, 0.15),
          },
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: hasOpenSession ? "success.light" : "error.light",
          }}
        />
        <Typography
          className="cash-pill-label"
          sx={{
            fontSize: isMobile ? 12 : 13,
            fontWeight: 700,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          noWrap
        >
          {label}
        </Typography>
        {!isMobile ? <KeyboardArrowDown fontSize="small" sx={{ opacity: 0.7, flexShrink: 0 }} /> : null}
      </ButtonBase>

      <Popover
        open={openMenu}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <PaperContent
          hasOpenSession={hasOpenSession}
          cashRegisterName={cashRegisterName}
          openedAt={openedAt}
          openedByName={openedByName}
          isOwnSession={isOwnSession}
          canRunAction={canRunAction}
          canCreateMovement={canCreateMovement}
          onActionClick={() => {
            handleClose();
            onActionClick?.();
          }}
          onMovementClick={() => {
            handleClose();
            onMovementClick?.();
          }}
        />
      </Popover>
    </>
  );
}

function PaperContent({
  hasOpenSession,
  cashRegisterName,
  openedAt,
  openedByName,
  isOwnSession,
  onActionClick,
  onMovementClick,
  canRunAction,
  canCreateMovement,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 300,
        maxWidth: 320,
        p: 1.25,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: "0 8px 18px rgba(30,34,24,0.10)",
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                lineHeight: 1.05,
                color: "text.primary",
              }}
            >
              CAJA
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                fontSize: "0.92rem",
                color: "text.secondary",
                lineHeight: 1.2,
              }}
            >
              {hasOpenSession
                ? `Caja abierta por ${openedByName || "usuario sin nombre"}`
                : "Sin sesion abierta"}
            </Typography>
            {hasOpenSession && openedAt ? (
              <Typography sx={{ mt: 0.25, fontSize: "0.8rem", color: "text.secondary" }}>
                Desde {formatDateTime(openedAt)}{isOwnSession ? "" : " - sesion ajena"}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 1,
              py: 0.4,
              borderRadius: 999,
              fontSize: "0.82rem",
              fontWeight: 800,
              lineHeight: 1,
              textTransform: "uppercase",
              color: hasOpenSession ? "success.dark" : "text.secondary",
              bgcolor: hasOpenSession ? "rgba(46,125,50,0.10)" : "rgba(30,34,24,0.06)",
              border: hasOpenSession
                ? "1px solid rgba(46,125,50,0.18)"
                : "1px solid rgba(30,34,24,0.08)",
            }}
          >
            {hasOpenSession ? "Abierta" : "Cerrada"}
          </Box>
        </Stack>

        <Divider sx={{ my: 0.25 }} />

        <Stack direction="row" justifyContent="flex-end" spacing={0.75}>
          {hasOpenSession && canCreateMovement ? (
            <Button
              variant="text"
              size="small"
              onClick={onMovementClick}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.45,
                fontSize: "0.8rem",
                fontWeight: 700,
                borderRadius: 1.5,
                textTransform: "none",
                color: "text.secondary",
              }}
            >
              Egreso
            </Button>
          ) : null}

          {canRunAction ? (
            <Button
              onClick={onActionClick}
              size="small"
              variant="contained"
              color={hasOpenSession ? "error" : "success"}
              sx={{
                minWidth: 88,
                px: 1.4,
                py: 0.55,
                minHeight: 34,
                borderRadius: 999,
                fontSize: "0.95rem",
                fontWeight: 800,
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              {hasOpenSession ? "Cerrar" : "Abrir"}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}




function AppBarComponent({ setDrawerOpen }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isDrawerViewport = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const { user, currentBranch, setCurrentBranch, effectivePerms = [], isSuperuser } = useAuth();

  const [branchAnchorEl, setBranchAnchorEl] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [posOpen, setPosOpen] = useState(false);

  const [loadingCash, setLoadingCash] = useState(false);
  const [activeGeneralInventory, setActiveGeneralInventory] = useState(null);
  const [submittingCash, setSubmittingCash] = useState(false);
  const [sessionData, setSessionData] = useState(EMPTY_SESSION_DATA);
  const [closeSummary, setCloseSummary] = useState(EMPTY_SESSION_DATA.summary);

  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [openForm, setOpenForm] = useState({
    opening_float: "0.00",
    opening_note: "",
  });
  const [closeForm, setCloseForm] = useState({
    counted_cash: "0.00",
    counted_card: "0.00",
    counted_transfer: "0.00",
    closing_note: "",
  });
  const [movementSubmitting, setMovementSubmitting] = useState(false);
  const [movementForm, setMovementForm] = useState({
    reason_type: "EXPENSE",
    payment_channel: "CASH",
    amount: "",
    description: "",
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const branches = useMemo(() => {
    const userBranches = Array.isArray(user?.user_branches) ? user.user_branches : [];
    return userBranches.map((item) => {
      const branch = item?.branch || {};
      return {
        id: branch?.id,
        name: branch?.alias || branch?.name || "Sucursal",
        current: currentBranch?.id === branch?.id || item?.current === true,
      };
    });
  }, [currentBranch?.id, user?.user_branches]);

  const branchId = currentBranch?.id || null;
  const currentBranchName = currentBranch?.alias || currentBranch?.name || "Sucursal";
  const displayName = user?.company?.name || "Pawsi Patitas";
  const logoSrc = fallbackLogo;
  const hasOpenSession = Boolean(sessionData?.session && sessionData.session.status === "OPEN");
  const openedById = sessionData?.session?.opened_by_id;
  const isOwnCashSession = hasOpenSession && Number(openedById) === Number(user?.id);
  const canCreateMovement = hasPermission(effectivePerms, "cash_movement_create", isSuperuser);
  const canOpenSession = hasPermission(effectivePerms, "cash_open", isSuperuser);
  const canCloseOwnCash = hasPermission(effectivePerms, "cash_close", isSuperuser);
  const canManageAnyCashSession = hasPermission(effectivePerms, "cash_session_manage", isSuperuser);
  const canUseQuickSale = hasPermission(effectivePerms, "sale_create", isSuperuser);
  const canOpenGeneralInventory = hasAnyGeneralInventoryPermission(effectivePerms, isSuperuser);
  const canCaptureGeneralFromAppBar =
    hasPermission(effectivePerms, "inventory_general_count", isSuperuser) ||
    hasPermission(effectivePerms, "inventory_general_adjust", isSuperuser);
  const cashOpenBlockedByGeneralInventory = Boolean(activeGeneralInventory?.id && !hasOpenSession);
  const canRunCashAction = hasOpenSession
    ? canCloseOwnCash && (isOwnCashSession || canManageAnyCashSession)
    : canOpenSession && !cashOpenBlockedByGeneralInventory;
  const closeExpectedTotal =
    Number(closeSummary?.expected_cash || 0) +
    Number(closeSummary?.expected_card || 0) +
    Number(closeSummary?.expected_transfer || 0);
  const cashPillMode = isMdUp ? "desktop" : isSm ? "tablet" : "mobile";
  const quickSaleLabel = isMdUp ? "Venta rapida" : isSm ? "Venta" : "";
  const showBranchChip = !isXs;
  const showCollapsedBranch = isXs;
  const isQuickSaleIconOnly = quickSaleLabel.length === 0;

  const handleHome = () => navigate("/");

  const handleOpenBranches = (event) => {
    setBranchAnchorEl(event.currentTarget);
  };

  const handleCloseBranches = () => {
    setBranchAnchorEl(null);
  };

  const handleSwitchBranch = (branchSelectedId) => {
    setCurrentBranch(branchSelectedId);
    setBranchAnchorEl(null);
  };

  const openActiveGeneralInventory = () => {
    if (!activeGeneralInventory?.id || !canOpenGeneralInventory) return;
    const status = String(activeGeneralInventory?.status || "").toUpperCase();
    if (status === "DRAFT" && canCaptureGeneralFromAppBar) {
      navigate(`/inventory/counts/general/capture?count_id=${activeGeneralInventory.id}`);
      return;
    }
    navigate(`/inventory/counts/${activeGeneralInventory.id}/review`);
  };

  const getInitials = () => {
    const name = displayName;
    return name
      .split(" ")
      .map((w) => w?.[0] || "")
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const syncCloseFormFromSummary = useCallback((summary) => {
    const nextSummary = summary || EMPTY_SESSION_DATA.summary;
    setCloseSummary(nextSummary);
    setCloseForm((prev) => ({
      ...prev,
      counted_cash: toInputMoney(nextSummary.expected_cash),
      counted_card: toInputMoney(nextSummary.expected_card),
      counted_transfer: toInputMoney(nextSummary.expected_transfer),
    }));
  }, []);

  const loadCurrentSession = useCallback(async () => {
    if (!branchId) {
      setSessionData(EMPTY_SESSION_DATA);
      setCloseSummary(EMPTY_SESSION_DATA.summary);
      return;
    }

    setLoadingCash(true);
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      const normalized = normalizeCurrentSession(data);
      setSessionData(normalized);

      if (normalized.session) {
        syncCloseFormFromSummary(normalized.summary);
      } else {
        setCloseSummary(EMPTY_SESSION_DATA.summary);
        setOpenForm((prev) => ({
          ...prev,
          opening_float: toInputMoney(normalized.summary.opening_float),
        }));
      }
    } catch {
      setSessionData(EMPTY_SESSION_DATA);
      setCloseSummary(EMPTY_SESSION_DATA.summary);
    } finally {
      setLoadingCash(false);
    }
  }, [branchId, syncCloseFormFromSummary]);

  const loadActiveGeneralInventory = useCallback(async () => {
    if (!branchId) {
      setActiveGeneralInventory(null);
      return;
    }
    try {
      const data = await inventoryService.getCountSettings({ branch_id: branchId });
      setActiveGeneralInventory(data?.active_general_inventory || null);
    } catch {
      setActiveGeneralInventory(null);
    }
  }, [branchId]);

  useEffect(() => {
    loadCurrentSession();
  }, [loadCurrentSession]);

  useEffect(() => {
    loadActiveGeneralInventory();
  }, [loadActiveGeneralInventory]);

  useEffect(() => {
    if (!branchId) return undefined;
    const handleFocus = () => {
      loadActiveGeneralInventory();
    };
    const intervalId = window.setInterval(loadActiveGeneralInventory, 30000);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [branchId, loadActiveGeneralInventory]);

  useEffect(() => {
    const openPos = () => {
      if (!canUseQuickSale) return;
      setPosOpen(true);
    };

    const onKeyDown = (event) => {
      if (event.key === "F2" && !event.repeat) {
        event.preventDefault();
        openPos();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("open-pos-dialog", openPos);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("open-pos-dialog", openPos);
    };
  }, [canUseQuickSale]);

  useEffect(() => {
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (!branchId || (payloadBranchId && payloadBranchId !== branchId)) return;
      loadCurrentSession();
    });

    return unsubscribe;
  }, [branchId, loadCurrentSession]);

  const openCashActionDialog = async () => {
    if (loadingCash || submittingCash || !branchId) return;
    if (!hasOpenSession && cashOpenBlockedByGeneralInventory) {
      showSnack("No puedes abrir caja durante inventario general.", "warning");
      return;
    }
    if (!canRunCashAction) return;
    if (hasOpenSession) {
      if (sessionData?.session?.id) {
        setLoadingCash(true);
        try {
          const detail = await cashService.getSession(sessionData.session.id, { branch_id: branchId });
          const normalizedDetail = normalizeSessionDetail(detail);
          syncCloseFormFromSummary(normalizedDetail.summary);
        } catch {
          showSnack("No se pudo actualizar el total esperado de la sesion.", "warning");
        } finally {
          setLoadingCash(false);
        }
      }
      setCloseDialogOpen(true);
    } else {
      setOpenDialogOpen(true);
    }
  };

  const openMovementDialog = () => {
    if (!branchId || !hasOpenSession || loadingCash || submittingCash || !canCreateMovement) return;
    setMovementDialogOpen(true);
  };

  const handleOpenSession = async () => {
    if (!branchId) return;
    if (!canOpenSession) {
      showSnack("No autorizado para abrir caja.", "warning");
      return;
    }
    if (cashOpenBlockedByGeneralInventory) {
      showSnack("No puedes abrir caja durante inventario general.", "warning");
      return;
    }

    setSubmittingCash(true);
    try {
      await cashService.openSession({
        branch_id: branchId,
        opening_float: openForm.opening_float || "0",
        opening_note: openForm.opening_note || "",
      });
      setOpenDialogOpen(false);
      emitCashSessionChanged({ branchId, action: "open" });
      await loadCurrentSession();
      showSnack("Caja abierta correctamente.", "success");
    } catch (error) {
      const statusCode = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (statusCode === 409) {
        if (detail && !error?.response?.data?.session) {
          await loadActiveGeneralInventory();
          showSnack(detail, "error");
          return;
        }
        setOpenDialogOpen(false);
        emitCashSessionChanged({ branchId, action: "open_existing" });
        await loadCurrentSession();
        showSnack("La caja ya estaba abierta.", "info");
        return;
      }
      showSnack(detail || "No se pudo abrir la caja.", "error");
    } finally {
      setSubmittingCash(false);
    }
  };

  const handleCloseSession = async () => {
    if (!branchId || !sessionData?.session?.id) {
      showSnack("No hay una sesion abierta para cerrar.", "warning");
      return;
    }
    if (!canCloseOwnCash) {
      showSnack("No autorizado para cerrar caja.", "warning");
      return;
    }

    setSubmittingCash(true);
    try {
      await cashService.closeSession(sessionData.session.id, {
        branch_id: branchId,
        counted_cash: closeForm.counted_cash || "0",
        counted_card: closeForm.counted_card || "0",
        counted_transfer: closeForm.counted_transfer || "0",
        closing_note: closeForm.closing_note || "",
      });
      setCloseDialogOpen(false);
      emitCashSessionChanged({ branchId, action: "close" });
      await loadCurrentSession();
      showSnack("Caja cerrada correctamente.", "success");
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo cerrar la caja.";
      showSnack(detail, "error");
    } finally {
      setSubmittingCash(false);
    }
  };

  const handleCreateMovement = async () => {
    if (!branchId || !sessionData?.session?.id) return;
    if (!canCreateMovement) {
      showSnack("No autorizado para registrar egresos.", "warning");
      return;
    }
    if (!movementForm.amount || Number(movementForm.amount) <= 0) {
      showSnack("El monto del egreso debe ser mayor a 0.", "warning");
      return;
    }

    setMovementSubmitting(true);
    try {
      const createdMovement = await cashService.createMovement({
        branch_id: branchId,
        cash_session_id: sessionData.session.id,
        movement_type: "OUT",
        reason_type: movementForm.reason_type,
        payment_channel: movementForm.payment_channel,
        amount: movementForm.amount,
        description: movementForm.description || "",
      });
      setMovementDialogOpen(false);
      setMovementForm({
        reason_type: "EXPENSE",
        payment_channel: "CASH",
        amount: "",
        description: "",
      });
      emitCashSessionChanged({ branchId, action: "movement_created", sessionId: sessionData.session.id });
      await loadCurrentSession();
      showSnack("Egreso registrado correctamente.", "success");
      if (createdMovement?.warning) {
        showSnack(createdMovement.warning, "warning");
      }
    } catch {
      showSnack("No se pudo registrar el egreso.", "error");
    } finally {
      setMovementSubmitting(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        color="primary"
        elevation={0}
        sx={{
          zIndex: isDrawerViewport ? 1100 : 1300,
          height: 64,
          bgcolor: (t) => t.palette.primary.main,
          color: (t) => t.palette.primary.contrastText,
          borderBottom: (t) => `1px solid ${t.palette.primary.dark}`,
          backdropFilter: "blur(10px)",
          overflow: "hidden",
        }}
      >
        <Toolbar
          sx={{
            height: 64,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.8, sm: 1, md: 1.25 },
            px: { xs: 1.1, sm: 1.3, md: 1.8 },
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.8, sm: 0.95, md: 1.2 },
              minWidth: 0,
              flexShrink: 0,
              position: "relative",
              zIndex: 3,
            }}
          >
            {isDrawerViewport && (
              <IconButton
                edge="start"
                onClick={(event) => {
                  event.stopPropagation();
                  if (typeof setDrawerOpen === "function") {
                    setDrawerOpen(true);
                  }
                }}
                sx={{
                  color: "inherit",
                  flexShrink: 0,
                  position: "relative",
                  zIndex: 4,
                  p: { xs: 0.85, sm: 0.95 },
                  mr: { xs: 0.35, sm: 0.2 },
                  "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.14) },
                }}
              >
                <MenuIcon fontSize={isXs ? "small" : "medium"} />
              </IconButton>
            )}

            <Box
              onClick={handleHome}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.9, sm: 0.95, md: 1.2 },
                cursor: "pointer",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {logoSrc && !imgError ? (
                <Box
                  component="img"
                  src={logoSrc}
                  alt="Logo"
                  onError={() => setImgError(true)}
                  sx={{
                    height: { xs: 28, sm: 31, md: 36 },
                    width: "auto",
                    objectFit: "contain",
                    filter: "contrast(1.02) saturate(1.02)",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 31, md: 34 },
                    height: { xs: 28, sm: 31, md: 34 },
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 800,
                  }}
                >
                  {getInitials()}
                </Avatar>
              )}

              {isMdUp && (
                <Typography
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  noWrap
                >
                  {displayName}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 0.6, sm: 0.75, md: 0.9 },
              minWidth: 0,
              ml: "auto",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
            }}
          >
            {showBranchChip ? (
              <Tooltip title="Cambiar sucursal">
                <Chip
                  onClick={handleOpenBranches}
                  label={currentBranchName}
                  variant="outlined"
                  size={isSm ? "small" : "medium"}
                  sx={{
                    maxWidth: { sm: 180, md: 220 },
                    minWidth: 0,
                    flexShrink: 1,
                    height: { sm: 30, md: 34 },
                    borderRadius: 20,
                    borderColor: alpha(theme.palette.common.white, 0.25),
                    bgcolor: alpha(theme.palette.common.white, 0.08),
                    color: "inherit",
                    fontWeight: 600,
                    fontSize: { sm: 12, md: 13 },
                    "& .MuiChip-label": {
                      display: "block",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: { sm: 1.05, md: 1.2 },
                    },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.15),
                    },
                  }}
                />
              </Tooltip>
            ) : null}

            {showCollapsedBranch ? (
              <Tooltip title="Cambiar sucursal">
                <IconButton
                  onClick={handleOpenBranches}
                  size="small"
                  sx={{
                    color: "inherit",
                    border: `1px solid ${alpha(theme.palette.common.white, 0.24)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.08),
                    width: 30,
                    height: 30,
                    flexShrink: 0,
                    "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.15) },
                  }}
                >
                  <Business fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            {activeGeneralInventory?.id ? (
              <Tooltip title="Inventario general activo">
                <Chip
                  onClick={canOpenGeneralInventory ? openActiveGeneralInventory : undefined}
                  label="Inventario Activo"
                  color="error"
                  size={isSm || isXs ? "small" : "medium"}
                  sx={{
                    maxWidth: { xs: 130, sm: 150, md: 170 },
                    minWidth: 0,
                    flexShrink: 0,
                    height: { xs: 30, md: 34 },
                    borderRadius: 20,
                    fontWeight: 800,
                    "& .MuiChip-label": {
                      display: "block",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: { xs: 0.9, md: 1.2 },
                    },
                  }}
                />
              </Tooltip>
            ) : null}

            <CashPill
              hasOpenSession={hasOpenSession}
              cashRegisterName={sessionData?.cash_register?.name || "Caja"}
              openedAt={sessionData?.session?.opened_at}
              openedByName={sessionData?.session?.opened_by_name}
              isOwnSession={isOwnCashSession}
              onActionClick={openCashActionDialog}
              onMovementClick={openMovementDialog}
              canRunAction={canRunCashAction}
              canCreateMovement={canCreateMovement}
              disabled={!branchId || loadingCash || submittingCash}
              mode={cashPillMode}
            />

            {canUseQuickSale ? (
              <Tooltip title="Venta rapida (F2)">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    aria-label="Venta rapida"
                    startIcon={isQuickSaleIconOnly ? undefined : <PointOfSaleOutlined fontSize="small" />}
                    onClick={() => setPosOpen(true)}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: "none",
                      minWidth: 0,
                      px: isQuickSaleIconOnly ? 0.9 : { sm: 1.2, md: 1.8 },
                      py: isQuickSaleIconOnly ? 0.55 : { sm: 0.55, md: 0.65 },
                      minHeight: { xs: 30, sm: 32, md: 36 },
                      fontSize: { sm: 12, md: 13 },
                      lineHeight: 1,
                      boxShadow: theme.shadows[isQuickSaleIconOnly ? 2 : 4],
                      transition: "all 0.2s",
                      flexShrink: 0,
                      "& .MuiButton-startIcon": {
                        mr: 0.5,
                        ml: 0,
                      },
                      "&:hover": {
                        transform: isMdUp ? "scale(1.02)" : "none",
                        boxShadow: theme.shadows[isQuickSaleIconOnly ? 3 : 6],
                      },
                    }}
                  >
                    {isQuickSaleIconOnly ? <PointOfSaleOutlined fontSize="small" /> : quickSaleLabel}
                  </Button>
                </span>
              </Tooltip>
            ) : null}

            {/* <Tooltip title="Notificaciones">
              <IconButton
                sx={{
                  color: "inherit",
                  "&:hover": { color: "inherit", bgcolor: alpha(theme.palette.common.white, 0.16) },
                }}
              >
                <Notifications />
              </IconButton>
            </Tooltip> */}

            <Box sx={{ flexShrink: 0 }}>
              <UserMenu />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <BranchSwitchMenu
        anchorEl={branchAnchorEl}
        openMenu={Boolean(branchAnchorEl)}
        handleCloseMenu={handleCloseBranches}
        branches={branches}
        onSelectBranch={handleSwitchBranch}
      />

      <POSDialog open={canUseQuickSale && posOpen} onClose={() => setPosOpen(false)} />

      <Dialog
        open={openDialogOpen && !cashOpenBlockedByGeneralInventory}
        onClose={() => setOpenDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Abrir caja</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Caja: {sessionData?.cash_register?.name || "Principal"}
            </Typography>
            <TextField
              label="Fondo inicial"
              size="small"
              type="number"
              value={openForm.opening_float}
              onChange={(event) =>
                setOpenForm((prev) => ({ ...prev, opening_float: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Nota apertura"
              size="small"
              multiline
              minRows={2}
              value={openForm.opening_note}
              onChange={(event) =>
                setOpenForm((prev) => ({ ...prev, opening_note: event.target.value }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogOpen(false)} disabled={submittingCash}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenSession}
            disabled={submittingCash || cashOpenBlockedByGeneralInventory}
          >
            Abrir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Cerrar caja</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Esperado efectivo: {toMoney(closeSummary?.expected_cash)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esperado tarjeta: {toMoney(closeSummary?.expected_card)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esperado transferencia: {toMoney(closeSummary?.expected_transfer)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Total esperado: {toMoney(closeExpectedTotal)}
            </Typography>
            <TextField
              label="Contado efectivo"
              size="small"
              type="number"
              value={closeForm.counted_cash}
              onChange={(event) =>
                setCloseForm((prev) => ({ ...prev, counted_cash: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Contado tarjeta"
              size="small"
              type="number"
              value={closeForm.counted_card}
              onChange={(event) =>
                setCloseForm((prev) => ({ ...prev, counted_card: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Contado transferencia"
              size="small"
              type="number"
              value={closeForm.counted_transfer}
              onChange={(event) =>
                setCloseForm((prev) => ({ ...prev, counted_transfer: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Nota cierre"
              size="small"
              multiline
              minRows={2}
              value={closeForm.closing_note}
              onChange={(event) =>
                setCloseForm((prev) => ({ ...prev, closing_note: event.target.value }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={submittingCash}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleCloseSession} disabled={submittingCash}>
            Cerrar caja
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={movementDialogOpen} onClose={() => setMovementDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Registrar egreso</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Razon</InputLabel>
              <Select
                label="Razon"
                value={movementForm.reason_type}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, reason_type: event.target.value }))
                }
              >
                {movementReasonOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select
                label="Canal"
                value={movementForm.payment_channel}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, payment_channel: event.target.value }))
                }
              >
                {movementChannelOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Monto"
              size="small"
              type="number"
              value={movementForm.amount}
              onChange={(event) =>
                setMovementForm((prev) => ({ ...prev, amount: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Descripcion"
              size="small"
              multiline
              minRows={2}
              value={movementForm.description}
              onChange={(event) =>
                setMovementForm((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovementDialogOpen(false)} disabled={movementSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreateMovement} disabled={movementSubmitting}>
            Guardar egreso
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}

export default AppBarComponent;


